/**
 * tryCoopAbility — shared helper for co-op ability activation.
 *
 * Co-op abilities (ResonantDrop, ChronosBubble) are called directly by scenes
 * (not via AbilityHolder.activateAbility), so Task 3's holder-level
 * ABILITY_BLOCKED emission does not cover them. This helper closes the gap:
 * on a failed canActivate() it emits ABILITY_BLOCKED with the highest-priority
 * reason — 'cooldown' when the ability is cooling down, 'range' when it is
 * ready but the partners are too far apart.
 *
 * @param ability  The co-op ability instance (must expose canActivate /
 *                 activate / cooldownRemaining).
 * @param abilityId  The string id used in the ABILITY_BLOCKED payload
 *                   (e.g. "resonantDrop", "chronosBubble").
 * @returns true when the ability activated successfully; false otherwise.
 */
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";

export interface CoopAbilityLike {
  canActivate(): boolean;
  activate(): boolean;
  cooldownRemaining: number;
}

export interface TryCoopAbilityOptions {
  /**
   * Whether the ability has been earned yet. Co-op abilities are Elder
   * rewards: Chronos Bubble, for example, is registered on the L2 scene
   * from frame one (so the activation path exists for the post-unlock
   * finale) but is narratively granted only by Elder Gus at the apex.
   * Pass `false` to block activation with an ABILITY_BLOCKED 'locked'
   * signal (HUD shows "Not unlocked yet"). Defaults to unlocked so
   * levels whose kit includes the ability from entry need no change.
   */
  unlocked?: boolean;
}

export function tryCoopAbility(
  ability: CoopAbilityLike,
  abilityId: string,
  options?: TryCoopAbilityOptions,
): boolean {
  if (options?.unlocked === false) {
    gameEvents.emit(GameEvents.ABILITY_BLOCKED, {
      ownerId: "coop",
      abilityId,
      reason: "locked",
    });
    return false;
  }

  if (ability.canActivate()) {
    ability.activate();
    return true;
  }

  // Determine the highest-priority block reason.
  // cooldown > 0  → the timer hasn't cleared yet.
  // otherwise     → ability is off cooldown but canActivate() still returned
  //                 false, which for co-op abilities means the partner
  //                 distance check failed → 'range'.
  const reason: "cooldown" | "range" =
    ability.cooldownRemaining > 0 ? "cooldown" : "range";

  gameEvents.emit(GameEvents.ABILITY_BLOCKED, {
    ownerId: "coop",
    abilityId,
    reason,
  });

  return false;
}
